/**
 * The summary Kelsey is emailed when a job is sent to her to close — ADR 0273.
 *
 * Everything the rep was shown in the pop-up, the same words, and that they
 * said yes to all five. Plain text: it leaves the rep's own Gmail.
 */

import type { CloseoutReview } from "./closeout.js";

const plural = (n: number, one: string, many = `${one}s`): string =>
  `${n} ${n === 1 ? one : many}`;

export interface CloseoutEmailInput {
  review: CloseoutReview;
  customer: string;
  address: string;
  jobNumber: number | string | null;
  repName: string | null;
  sentBy: string;
  /** When it was sent, already formatted for a person. */
  sentOn: string;
  /** A link straight to the job. */
  link: string;
}

/**
 * The summary Kelsey gets: everything the rep was shown, and that they said
 * yes to all five.
 */
export function closeoutEmail(input: CloseoutEmailInput): {
  subject: string;
  text: string;
} {
  const { review } = input;
  const number = input.jobNumber == null ? "" : ` — job ${input.jobNumber}`;
  const lines: string[] = [
    `${input.sentBy} sent this job to be closed on ${input.sentOn}.`,
    "",
    `Customer: ${input.customer}`,
    `Address: ${input.address}`,
    `Job: ${input.jobNumber ?? "no number"}`,
    `Sales rep: ${input.repName ?? "none"}`,
    `Open the job: ${input.link}`,
  ];
  const count = review.warnings.length;
  lines.push(
    "",
    count === 0
      ? "Nothing was flagged."
      : `${plural(count, "thing")} flagged, listed under each heading.`,
  );
  if (review.general.length > 0) {
    lines.push("");
    for (const text of review.general) lines.push(`! ${text}`);
  }
  for (const s of review.sections) {
    lines.push("", s.question.toUpperCase(), `Confirmed by ${input.sentBy}.`);
    for (const fact of s.facts) lines.push(`- ${fact}`);
    for (const warning of s.warnings) lines.push(`! ${warning}`);
  }
  return {
    subject: `Close out: ${input.customer}${number}`,
    text: lines.join("\n"),
  };
}
